import React, { Suspense, useRef, useState, useEffect, useMemo } from 'react'
import ReactDOM from 'react-dom';
import './index.css';
import reportWebVitals from './reportWebVitals';
import * as THREE from 'three';
import { Stats, Html, Center } from '@react-three/drei';
import { Canvas, extend, useThree, useFrame } from '@react-three/fiber'
import { useLoader } from '@react-three/fiber'
import { useTexture } from '@react-three/drei';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader'

import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer'
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass'
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass'
import { MeshBasicMaterial } from 'three';

extend({ EffectComposer, RenderPass, UnrealBloomPass });

const wishes = ["Everyone agrees you are the best.", "Travel is in your future.", "You will do well to advance your career.", "If you have an idea, make it into reality.", "A gathering of friends will bring you luck.", "Fall for someone who's not your type.", "Love is right around the corner.", "Respect your elders. You could inherit a large sum of money.", "An exciting adventure awaits you.", "Somebody appreciates the unique you.", "Your hard work will be rewarded.", "You have good reason to be self-confident.", "Fame and fortune lie ahead.", "To achieve wisdom, you must first desire it.", "Don't hesitate to tackle a difficult problem.", "You excel at pleasing others.", "Do your job to the best of your ability.", "Look in the mirror without admiring your reflection.", "Everything has beauty, but not everyone can see it.", "Avoid negative people to stay positive.", "The best way to predict the future is to create it.", "Pursue your dreams with vigor.", "Stop letting other people stand in your way.", "Be thankful, even if your life is not perfect.", "Someone thinks the world of you.", "Your smile lights up someone else's day.", "Lead by example, not by words alone.", "Trust your friends, but keep your eyes open.", "Seek out a new environment if you're stuck in a rut.", "Everything will work out for the best.", "Go with your gut feeling.", "Stop procrastinating. Make a decision already.", "Don't worry what others think of you.", "Take care of yourself first. Then help others.", "You might want to run, but you should stay and fight.", "A healthy body will benefit you for life.", "Your home will be filled with peace and harmony.", "Things may seem much worse than they are.", "You can learn much from people who are different from you.", "Move quickly. Now is the time to make progress.", "True happiness makes us wise.", "To be idle is to be foolish.", "Show everyone what you can do.", "It may be difficult, but it will be worth it in the end.", "Happiness may be right under your nose.", "You will soon receive a surprise from a lover.", "Don't wait for success to come - go find it!", "Face the truth with dignity.", "Work with your destiny. Stop trying to outrun it.", "Learn from your mistakes. Try not to make them again.", "Focus on your health and well-being now. It won't wait.", "Just have fun.", "If you haven't got it, just fake it!", "Work with what you have.", "Be the change you want to see in the world.", "Free your mind, and the rest will follow.", "Work first, but make sure to play later.", "Pay attention to your family. Don't take them for granted.", "Worry does not beget change.", "Get ready for a life-changing event!", "Learn how to do something new today.", "True love is on its way. Make room!", "Open your mind and your heart to good things.", "Be at peace with yourself.", "Out of all 200 wishes you got the secret one! You are one lucky person", "Tomorrow is another day.", "If you can dream it, you can become it!"];

function Effect({ children }) {
  const composer = useRef()
  const { scene, gl, size, camera } = useThree()
  const aspect = useMemo(() => new THREE.Vector2(size.width, size.height), [size])
  useEffect(() => void composer.current.setSize(size.width, size.height), [size])
  useFrame(() => composer.current.render(), 1)
  return (
    <>
    {children}
    <effectComposer ref={composer} args={[gl]}>
      <renderPass attachArray="passes" scene={scene} camera={camera} />
      <unrealBloomPass attachArray="passes" args={[aspect, 5, 1.2, 0]} />
    </effectComposer>
    </>
  )
}

function Particles({ count }) {
  const mesh = useRef()
  const { mouse } = useThree()

  const dummy = useMemo(() => new THREE.Object3D(), [])
  
  const particles = useMemo(() => {
    const temp = []
    for (let i = 0; i < count; i++) {
      const t = Math.random() * 10
      const factor = 1 + Math.random() * 10
      const speed = 0.01 + Math.random() / 200
      const start = new THREE.Vector3(Math.random() * 20 - 10,
                                      Math.random() * 20 - 10,
                                      Math.random() * 20 - 10);
      temp.push({ t, factor, speed, start, x: 0, y: 0 })
    }
    return temp
  }, [count])
  
  useFrame(state => {
    
    // Go through each particle
    particles.forEach((particle, i) => {
      let { t, factor, speed, start} = particle
      t = particle.t += speed / 2
      const a = Math.cos(t) + Math.sin(t * 1) / 10
      const b = Math.sin(t) + Math.cos(t * 2) / 10
      const s = Math.cos(t)
      particle.x += (mouse.x - particle.x) * 0.01
      particle.y += (mouse.y * -1 - particle.y) * 0.01
      // Change position
      dummy.position.set(
        (particle.x / 10) * a + start.x + Math.cos((t / 10) * factor) + (Math.sin(t * 1) * factor) / 10,
        (particle.y / 10) * b + start.y + Math.sin((t / 10) * factor) + (Math.cos(t * 2) * factor) / 10,
        (particle.y / 10) * b + start.z + Math.cos((t / 10) * factor) + (Math.sin(t * 3) * factor) / 10
      )
      dummy.scale.set(s, s, s)
      dummy.rotation.set(s, s, s)
      dummy.updateMatrix()
      // And apply the matrix to the instanced mesh
      mesh.current.setMatrixAt(i, dummy.matrix)
    })
    mesh.current.instanceMatrix.needsUpdate = true
  })
  return (
    <>
      <instancedMesh ref={mesh} args={[null, null, count]}>
        <dodecahedronBufferGeometry attach="geometry" args={[0.08, 0]} />
        <meshPhongMaterial attach="material" color="#050505" />
      </instancedMesh>
    </>
  )
}



function InnerCircle({ count }) {
  const mesh = useRef()
  const { mouse, camera } = useThree()

  const dummy = useMemo(() => new THREE.Object3D(), [])
  
  const particles = useMemo(() => {
    const temp = []
    for (let i = 0; i < count; i++) {
      const t = Math.random() * 10
      const speed = 0.01 + Math.random() / 200
      const start = new THREE.Vector3(Math.random() * 20 - 10,
                                      Math.random() * 20 - 10,
                                      Math.random() * 20 - 10);
      temp.push({ t, speed, start, x: 0, y: 0 })
    }
    return temp
  }, [count])
  
  useFrame(state => {
    camera.position.set(mouse.x, mouse.y, 5);
    camera.lookAt(new THREE.Vector3(0,0,0));
    
    // Go through each particle
    particles.forEach((particle, i) => {
      let { t, speed, start} = particle
      t = particle.t += speed / 2
      const a = Math.cos(t + start.x) * Math.sin(t + start.y)
      const b = Math.sin(t + start.x) * Math.sin(t + start.y) 
      const s = Math.cos(t + start.y)
      // Change position
      dummy.position.set(a,b,s)
      dummy.scale.set(s, s, s)
      dummy.rotation.set(s, s, s)
      dummy.updateMatrix()
      // And apply the matrix to the instanced mesh
      mesh.current.setMatrixAt(i, dummy.matrix)
    })
    mesh.current.instanceMatrix.needsUpdate = true
  })
  return (
    <>
      <instancedMesh ref={mesh} args={[null, null, count]}>
        <dodecahedronBufferGeometry attach="geometry" args={[0.05, 0]} />
        <meshBasicMaterial attach="material" color="#996699" />
      </instancedMesh>
    </>
  )
}

function Hands()
{
  const gltf = useLoader(GLTFLoader, './arms.glb');
  const ref = useRef();
  gltf.scene.scale.set(3,3,3);
  gltf.scene.children[0].material = new THREE.MeshStandardMaterial({color: "#F0DDD7"});

  
  useFrame(({clock}) => {
    ref.current.rotation.z = Math.sin(clock.elapsedTime)+0.5;
  });

  return (
    <primitive ref={ref} object={gltf.scene} />
  )
}

function Sphere()
{
  const texture = useTexture("./noise.png");
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  
  useFrame(({clock}) => {
    texture.offset.y = clock.elapsedTime / 10;
  });

  return(
    <mesh position={[0, 0, 0]}>
      <sphereGeometry attach="geometry" args={[0.4, 16, 16]} />
      <meshBasicMaterial
        attach="material"
        map={texture}
      />
    </mesh>
  )
}

function Head()
{
  return(
    <>
    <mesh position={[0, 1.5, -0.1]} scale={[1, 1.2, 1]}>
      <sphereBufferGeometry attach="geometry" args={[0.5, 16, 16]} />
      <meshBasicMaterial attach="material" color="black" transparent={true} opacity={0.2} depthTest={false}/>
    </mesh>

    <mesh position={[-0.2, 1.5, 0]} rotation={[Math.PI / 2, -0.3,Math.PI / 3]}>
      <circleBufferGeometry attach="geometry" args={[0.1, 3]} />
      <meshBasicMaterial attach="material" color="red" />
    </mesh>
    
    <mesh position={[0.2, 1.5, 0]} rotation={[Math.PI / 2, 0.3,0]}>
      <circleBufferGeometry attach="geometry" args={[0.1, 3]} />
      <meshBasicMaterial attach="material" color="red" />
    </mesh>
    </>
  )
}

ReactDOM.render(
  <Canvas>
  <color attach="background" args={"#050105"} />
    <Effect>
      <ambientLight intensity = {0.1} />
      <pointLight position={[0,0,0]} intensity={10}/>
      
      <Suspense fallback={null}>
        <Particles count={1000}/>
        <InnerCircle count={100}/>
        <Sphere />
        <Hands />
        <Head />
      </Suspense>
      <Html position={[-1.3,-1,0]}>
        <div style={{color: 'white', width: '250px', textAlign: 'center'}}>{wishes[Math.floor(Math.random() * wishes.length)]}</div>
      </Html>
    </Effect>
    {/* <Stats /> */}
  </Canvas>,
  document.getElementById('root'),
)

reportWebVitals();
